(() => {
  const thumbSelector = '[data-gallery-image], [data-gallery-video], [data-charm-gallery], [data-charm-show-preview]';
  class ProductMedia extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.stage = this.querySelector('[data-product-media]');
      this.strip = this.querySelector('.studio-product-thumbs');
      this.dialog = this.closest('.shopify-section')?.querySelector('[data-product-dialog]');
      if (!this.stage) return;
      this.addEventListener('click', event => {
        const videoThumb = event.target.closest('[data-gallery-video]');
        if (videoThumb) { this.showVideo(videoThumb); return; }
        if (this.video && this.stage.contains(event.target)) return;
        if (Date.now() < this.suppressClickUntil && this.stage.contains(event.target)) {
          event.preventDefault(); return;
        }
        const step = event.target.closest('[data-media-step]');
        if (step) this.move(Number(step.dataset.mediaStep));
        if (event.target.closest('[data-product-zoom]') || this.stage.contains(event.target)) this.openViewer(event.target.closest('button'));
      }, options);
      this.addEventListener('keydown', event => {
        if (!event.target.closest(thumbSelector)) return;
        this.navigateKey(event);
      }, options);
      this.addEventListener('preview-ready', () => this.refresh(), options);
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.video?.pause(); }, options);
      this.addSwipe(this.stage, options);
      if (this.dialog) {
        this.dialog.addEventListener('click', event => {
          if (Date.now() < this.suppressClickUntil) return;
          const step = event.target.closest('[data-media-step]');
          if (step) this.move(Number(step.dataset.mediaStep));
          if (event.target.closest('[data-product-dialog-close]') || event.target === this.dialog) this.dialog.close();
        }, options);
        this.dialog.addEventListener('keydown', event => {
          if (event.key === 'Tab') {
            const buttons = [...this.dialog.querySelectorAll('button:not([hidden]):not([disabled])')];
            const first = buttons[0], last = buttons[buttons.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
          } else this.navigateKey(event);
        }, options);
        this.dialog.addEventListener('close', () => {
          this.viewerSource = null;
          document.documentElement.classList.remove('pdp-viewer-open');
          this.returnFocus?.focus({preventScroll: true});
        }, options);
        this.addSwipe(this.dialog.querySelector('.pdp-image-dialog-body'), options);
      }
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this.stage, {subtree: true, attributes: true, attributeFilter: ['src', 'hidden']});
      if (this.strip) this.observer.observe(this.strip, {subtree: true, attributes: true, attributeFilter: ['aria-pressed', 'hidden']});
      this.dataset.mediaReady = 'true';
      this.refresh();
    }

    disconnectedCallback() {
      this.stage?.querySelectorAll('video').forEach(video => video.pause());
      if (this.dialog?.open) {
        this.dialog.close();
        document.documentElement.classList.remove('pdp-viewer-open');
      }
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }

    get items() { return [...this.querySelectorAll(thumbSelector)].filter(button => !button.hidden); }
    get canvas() { return this.stage.querySelector('canvas:not([hidden])'); }
    get video() { return this.stage.querySelector('[data-product-video]:not([hidden]) video'); }

    showVideo(button) {
      const wrapper = [...this.stage.querySelectorAll('[data-product-video]')].find(item => item.dataset.productVideo === button.dataset.galleryVideo);
      if (!wrapper) return;
      this.stage.querySelectorAll('[data-product-video]').forEach(item => { if (item !== wrapper) item.querySelector('video')?.pause(); item.hidden = item !== wrapper; });
      const photo = this.stage.querySelector(':scope > img');
      if (photo) photo.hidden = true;
      this.stage.dataset.videoActive = 'true';
      this.items.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      if (this.dialog?.open) { this.returnFocus = button; this.dialog.close(); }
      this.refresh();
      wrapper.querySelector('video')?.play().catch(() => {});
    }

    refresh() {
      const items = this.items;
      const preview = this.canvas;
      this.index = items.findIndex(button => button.getAttribute('aria-pressed') === 'true');
      if (this.index < 0 && items.length && !preview) {
        const source = this.stage.querySelector('img')?.src;
        const path = url => { try { return new URL(url, location.href).pathname; } catch { return ''; } };
        this.index = items.findIndex(button => path(button.dataset.galleryImage || button.dataset.charmGallery) === path(source));
        if (this.index < 0) this.index = 0;
        items[this.index].setAttribute('aria-pressed', 'true');
      }
      const total = Math.max(items.length, 1);
      const current = Math.max(this.index + 1, 1);
      const caption = preview ? this.dataset.previewLabel : `${current} / ${total}`;
      const label = preview ? caption : this.dataset.positionLabel?.replace('[current]', current).replace('[total]', total);
      const roots = this.dialog ? [this, this.dialog] : [this];
      roots.forEach(root => {
        root.querySelectorAll('[data-media-count]').forEach(counter => {
          if (counter.dataset.caption === caption) return;
          counter.dataset.caption = caption;
          counter.replaceChildren();
          const visible = document.createElement('span');
          visible.setAttribute('aria-hidden', 'true'); visible.textContent = caption;
          const accessible = document.createElement('span');
          accessible.className = 'visually-hidden'; accessible.textContent = label || caption;
          counter.append(visible, accessible);
        });
        root.querySelectorAll('[data-media-step]').forEach(button => button.hidden = items.length < 2);
      });
      const tools = this.querySelector('[data-media-tools]');
      if (tools) tools.hidden = false;
      const zoom = this.querySelector('[data-product-zoom]');
      if (zoom) zoom.hidden = !!this.video;
      const active = items[this.index];
      if (active && active !== this.activeThumb && this.strip) {
        const item = active.getBoundingClientRect(), strip = this.strip.getBoundingClientRect();
        const vertical = getComputedStyle(this.strip).flexDirection === 'column';
        if (vertical && (item.top < strip.top || item.bottom > strip.bottom)) this.strip.scrollTop += item.top - strip.top - (strip.height - item.height) / 2;
        if (!vertical && (item.left < strip.left || item.right > strip.right)) this.strip.scrollLeft += item.left - strip.left - (strip.width - item.width) / 2;
      }
      this.activeThumb = active;
      if (this.dialog?.open) this.updateViewer();
    }

    move(direction, absolute = false) {
      const items = this.items;
      if (items.length < 2) return;
      const index = absolute ? direction : (this.index + direction + items.length) % items.length;
      items[index]?.click();
      queueMicrotask(() => this.refresh());
    }

    navigateKey(event) {
      const keys = {ArrowLeft: -1, ArrowRight: 1};
      if (event.key in keys) { event.preventDefault(); this.move(keys[event.key]); }
      else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault(); this.move(event.key === 'Home' ? 0 : this.items.length - 1, true);
      } else return;
      if (!this.dialog?.open) this.items.find(button => button.getAttribute('aria-pressed') === 'true')?.focus({preventScroll: true});
    }

    addSwipe(element, options) {
      let start;
      element.addEventListener('pointerdown', event => {
        if (this.video || event.target.closest('video')) { start = null; return; }
        if (event.pointerType !== 'touch' || !event.isPrimary) { start = null; return; }
        start = {x: event.clientX, y: event.clientY};
      }, options);
      element.addEventListener('pointercancel', () => start = null, options);
      element.addEventListener('pointerup', event => {
        if (!start) return;
        const dx = event.clientX - start.x, dy = event.clientY - start.y;
        start = null;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.5 || this.items.length < 2) return;
        this.suppressClickUntil = Date.now() + 350;
        this.move(dx < 0 ? 1 : -1);
      }, options);
    }

    openViewer(opener) {
      if (this.video || !this.dialog?.showModal || !this.stage.querySelector('img, canvas')) return;
      this.returnFocus = opener || this.querySelector('[data-product-zoom]');
      this.updateViewer();
      this.dialog.showModal();
      document.documentElement.classList.add('pdp-viewer-open');
    }

    updateViewer() {
      const image = this.dialog.querySelector('[data-product-dialog-image]');
      const canvas = this.dialog.querySelector('[data-product-dialog-canvas]');
      const preview = this.canvas;
      if (image) image.hidden = !!preview;
      canvas.hidden = !preview;
      if (preview) {
        canvas.width = preview.width; canvas.height = preview.height;
        canvas.getContext('2d').drawImage(preview, 0, 0);
      } else {
        const source = this.stage.querySelector('img');
        if (image && source) {
          const target = source.src;
          if (this.viewerSource !== target) {
            this.viewerSource = target;
            const current = source.currentSrc || target;
            image.src = new URL(current).pathname === new URL(target).pathname ? current : target;
            if (image.src !== target) {
              const fullSize = new Image();
              fullSize.onload = () => {
                if (this.dialog.open && !this.canvas && this.viewerSource === target) image.src = target;
              };
              fullSize.src = target;
            }
          }
          image.alt = source.alt;
          image.loading = 'eager';
        }
      }
    }
  }
  if (!customElements.get('product-media')) customElements.define('product-media', ProductMedia);
})();
